import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs';

import {
  type FeatureAnnouncement,
  FeatureAnnouncementStoreService,
} from '@customer-portal/data-access/feature-announcements';
import { getToastContentBySeverity } from '@customer-portal/shared/helpers/custom-toast';
import { ToastSeverity } from '@customer-portal/shared/models';

import type { FeatureTourControlLabels, TourOutcome } from './tour';
import { FeatureTourService } from './tour';

@Injectable({ providedIn: 'root' })
export class FeatureAnnouncementCtaService {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly announcementStore = inject(FeatureAnnouncementStoreService);
  private readonly featureTourService = inject(FeatureTourService);

  execute(
    feature: FeatureAnnouncement,
    controlLabels?: FeatureTourControlLabels,
  ): void {
    if (feature.hasTour) {
      this.featureTourService
        .startTour(feature, controlLabels ?? this.getTourLabels())
        .pipe(take(1))
        .subscribe({
          next: (outcome) => this.handleTourOutcome(outcome),
          error: () => this.showActionError('actionUnavailable'),
        });

      return;
    }

    if (!feature.route) {
      this.showActionError('actionUnavailable');

      return;
    }

    this.navigateAndMarkSeen(feature.route, feature.featureKey);
  }

  openFeature(feature: FeatureAnnouncement): void {
    if (!feature.route) {
      this.showActionError('actionUnavailable');

      return;
    }

    this.navigateAndMarkSeen(feature.route, feature.featureKey);
  }

  private navigateAndMarkSeen(route: string, featureKey: string): void {
    this.router.navigateByUrl(route).then(
      (navigated) => {
        if (navigated) {
          this.markSeen(featureKey);
        } else {
          this.showActionError('actionUnavailable');
        }
      },
      () => this.showActionError('actionUnavailable'),
    );
  }

  private markSeen(featureKey: string): void {
    this.announcementStore.markFeatureAnnouncementSeen(featureKey);
  }

  private getTourLabels(): FeatureTourControlLabels {
    return {
      next: this.transloco.translate('whatsNew.tour.next'),
      previous: this.transloco.translate('whatsNew.tour.previous'),
      done: this.transloco.translate('whatsNew.tour.done'),
    };
  }

  private handleTourOutcome(outcome: TourOutcome): void {
    if (outcome === 'not-ready') {
      this.showActionError('tourNotReady');
    } else if (outcome === 'unavailable') {
      this.showActionError('actionUnavailable');
    }
  }

  private showActionError(
    messageKey: 'tourNotReady' | 'actionUnavailable',
  ): void {
    this.messageService.add({
      ...getToastContentBySeverity(ToastSeverity.Error),
      detail: this.transloco.translate(`whatsNew.${messageKey}`),
    });
  }
}
